
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";

// =========================================
// DEV ENDPOINTS (hardcoded)
// =========================================
const API_BASE = "https://api.pharmacegroup.co.uk";
const LEGACY_ROUTE = "/getStaffAttendanceLogsDev";     // legacy per-tenant logs (fixed DB on server)
const V2_ATT_ROUTE = "/getStaffAttendanceLogsV2Dev";   // v2 central logs -> legacy-shaped
const V2_BREAK_ROUTE = "/getStaffBreakLogsDev";        // v2 breaks keyed by attendanceLogId

const ALLOW_EDIT = false;

// -----------------------------------------
// Small helpers
// -----------------------------------------
const formatHHMMNoTZ = (dLike) => {
  if (!dLike) return "00:00";
  const d = dLike instanceof Date ? dLike : new Date(dLike);
  if (isNaN(d)) return "00:00";
  return d.toISOString().slice(11, 16);
};

const toNumOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const haversineMeters = (lat1, lon1, lat2, lon2) => {
  const a1 = toNumOrNull(lat1), b1 = toNumOrNull(lon1);
  const a2 = toNumOrNull(lat2), b2 = toNumOrNull(lon2);
  if (a1 === null || b1 === null || a2 === null || b2 === null) return null;
  const R = 6371000;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(a2 - a1);
  const dLon = toRad(b2 - b1);
  const la1 = toRad(a1), la2 = toRad(a2);
  const h = Math.sin(dLat/2)**2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon/2)**2;
  return Math.round(2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h)));
};

const diffHours = (start, end) => {
  if (!start || !end) return 0;
  const a = start instanceof Date ? start : new Date(start);
  const b = end instanceof Date ? end : new Date(end);
  if (isNaN(a) || isNaN(b)) return 0;
  const ms = b - a;
  const h = ms / 36e5;
  return Number.isFinite(h) && h > 0 ? h : 0;
};

// -----------------------------------------
// Component
// -----------------------------------------
const StaffDev = () => {
  const masked = useMaskNames();
  const [data, setData] = useState([]);                  // merged legacy + v2 synthetic entries
  const [breaksByAttId, setBreaksByAttId] = useState({}); // { attendanceLogId: [ {breakStart, breakEnd, notes?} ] }

  // filters
  const [selectedPharmacy, setSelectedPharmacy] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedYear, setSelectedYear] = useState("All");
  const [selectedStaff, setSelectedStaff] = useState("All");
  const [searchText, setSearchText] = useState("");
  const [showDetails, setShowDetails] = useState(true);
  const [onlyShowIssues, setOnlyShowIssues] = useState(false);

  // sorting
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("asc");

  // edit UI (kept intact - not wired to v2)
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editSessionIndex, setEditSessionIndex] = useState(-1);
  const [editInDT, setEditInDT] = useState("");
  const [editOutDT, setEditOutDT] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editAddNotes, setEditAddNotes] = useState("");
  const [lastChangeSummary, setLastChangeSummary] = useState("");
  const [editOutEditable, setEditOutEditable] = useState(true);

  // map modal
  const [mapOpen, setMapOpen] = useState(false);
  const [mapInfo, setMapInfo] = useState(null);
  const [mapMode, setMapMode] = useState("in");
  const mapDivRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const mapMarkersRef = useRef(null);

  const members = JSON.parse(localStorage.getItem("members") || "[]");
  const organisationName = localStorage.getItem("organisationName") || "Group";

  // -----------------------------------------
  // Styles
  // -----------------------------------------
  const staffLogsStyles = String.raw`
  .staff-logs { width: 100%; border-collapse: collapse; table-layout: fixed; border: 1px solid #cfd8e3; }
  .staff-logs th, .staff-logs td { border: 1px solid #d6dbe5; padding: 8px 10px; text-align: left; vertical-align: top; white-space: pre-line; }
  .staff-logs thead th { background: #eef2f7; border-bottom: 2px solid #c8d1e0; position: sticky; top: 0; z-index: 1; user-select: none; cursor: pointer; }
  .staff-logs thead th.nosort { cursor: default; }
  .staff-logs tbody tr:nth-child(odd) td { background-color: #fbfbfd; }
  .staff-logs td { word-break: break-word; }

  .seg { border-top: 1px dashed #c9cdd3 !important; }

  .edit-icon-btn { border: none; background: transparent; cursor: pointer; font-size: 12px; padding: 0 6px; color: #2563eb; }
  .edit-icon-btn:hover { text-decoration: underline; }

  .amber { background: #ffe8a3; }
  .nk { background: #ffe599; }

  .sort-ind { font-size: 11px; color: #475569; margin-left: 6px; }
  .th-wrap { display:flex; align-items:center; }
  .th-wrap .label { pointer-events: none; }

  .divider-row td { text-align: center; font-weight: 600; color: #374151; background: #f8fafc; border-top: 2px solid #1f2937 !important; border-bottom: 2px solid #1f2937 !important; }

  .dist-cell { cursor: pointer; text-decoration: underline; }
  .dist-coral { background: lightcoral !important; }

  .staff-logs tr.row-invalid td { background: lightcoral !important; }

  .modal-backdrop, .map-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.35); display: flex; align-items: center; justify-content: center; z-index: 9999; }
  .modal-card, .map-card { width: 760px; max-width: 96vw; background: #fff; border-radius: 8px; box-shadow: 0 20px 50px rgba(0,0,0,0.25); overflow: hidden; }
  .modal-header, .map-header { display:flex; align-items:center; justify-content:space-between; padding: 10px 12px; border-bottom: 1px solid #e5e7eb; background: #f9fafb; }
  .modal-title, .map-title { margin: 0; font-size: 16px; font-weight: 700; }
  .modal-body, .map-body { padding: 12px; }
  .modal-footer, .map-footer { padding: 12px; border-top: 1px solid #e5e7eb; display:flex; justify-content:flex-end; gap: 8px; }
  .btn { padding: 6px 10px; border: 1px solid #cbd5e1; border-radius: 6px; background: #fff; cursor: pointer; }
  .btn.primary { background: #2563eb; color: #fff; border-color: #1d4ed8; }

  .modal-header .btn, .map-header .btn { background: #111827; color: #fff; border-color: #111827; opacity: 1; }
  .modal-header .btn:hover, .map-header .btn:hover { filter: brightness(0.92); }

  #attendance-map { width: 100%; height: 420px; border: 1px solid #e5e7eb; border-radius: 6px; }
  .pharmacy-marker { font-size: 18px; line-height: 20px; text-align: center; }

  /* Hover card */
  .hover-wrap { position: relative; display: inline-block; }
  .hover-card {
    position: absolute;
    left: 0;
    top: calc(100% + 6px);
    min-width: 340px;
    max-width: 520px;
    background: #fff;
    border: 1px solid #cfd8e3;
    border-radius: 8px;
    box-shadow: 0 8px 30px rgba(0,0,0,0.18);
    padding: 10px 12px;
    z-index: 20;
    opacity: 0;
    transform: translateY(-3px);
    pointer-events: none;
    transition: opacity .15s ease, transform .15s ease;
    white-space: normal;
  }
  .hover-wrap:hover .hover-card { opacity: 1; transform: translateY(0); pointer-events: auto; }
  .hover-title { font-weight: 700; margin-bottom: 6px; }
  .hover-line { margin: 2px 0; font-size: 12px; }
  .hover-total { margin-top: 8px; padding-top: 8px; border-top: 1px dashed #cfd8e3; font-weight: 700; }
  `;

  // -----------------------------------------
  // Fetch (legacy + v2 + breaks)
  // -----------------------------------------
  const fetchAllLogs = useCallback(async () => {
    try {
      const groupId = localStorage.getItem("userId");
      if (!groupId) {
        console.warn("No groupId found in localStorage");
        return;
      }
      const authToken = localStorage.getItem("authToken") || localStorage.getItem("token");
      const headers = {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      };

      const [legacyRes, v2Res, brRes] = await Promise.all([
        fetch(`${API_BASE}${LEGACY_ROUTE}`, { method: "POST", headers, body: JSON.stringify({ groupId }) }),
        fetch(`${API_BASE}${V2_ATT_ROUTE}`, { method: "POST", headers, body: JSON.stringify({ groupId }) }),
        fetch(`${API_BASE}${V2_BREAK_ROUTE}`, { method: "POST", headers, body: JSON.stringify({ groupId }) }),
      ]);

      const legacyText = await legacyRes.text();
      const v2Text = await v2Res.text();
      const breaksText = await brRes.text();

      if (!legacyRes.ok) console.error("getStaffAttendanceLogsDev failed:", legacyRes.status, legacyText);
      if (!v2Res.ok) console.error("getStaffAttendanceLogsV2Dev failed:", v2Res.status, v2Text);
      if (!brRes.ok) console.error("getStaffBreakLogsDev failed:", brRes.status, breaksText);

      const legacyLogs = legacyText ? (JSON.parse(legacyText).logs || []) : [];
      const v2Logs = v2Text ? (JSON.parse(v2Text).logs || []) : [];
      const breaksArr = breaksText ? (JSON.parse(breaksText).breaks || []) : [];

      const map = {};
      for (const b of breaksArr) {
        const key = String(b.attendanceLogId || b.attendance_log_id || "");
        if (!key) continue;
        (map[key] ||= []).push({
          breakStart: b.breakStart,
          breakEnd: b.breakEnd,
          notes: b.notes || b.breakNotes || ""
        });
      }

      setData([...(Array.isArray(legacyLogs) ? legacyLogs : []), ...(Array.isArray(v2Logs) ? v2Logs : [])]);
      setBreaksByAttId(map);
    } catch (err) {
      console.error("Error fetching staff logs:", err);
    }
  }, []);

  useEffect(() => { fetchAllLogs(); }, [fetchAllLogs]);

  // -----------------------------------------
  // Anchored-month helpers
  // -----------------------------------------
  const getTimesheetDay = () => {
    const raw = localStorage.getItem("timesheet_day") || localStorage.getItem("timesheetDay") || "1";
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 1 && n <= 28 ? n : 1;
  };

  const clampStartOfDay = (y, m, d) => {
    const last = new Date(y, m + 1, 0).getDate();
    const dd = Math.max(1, Math.min(d, last));
    return new Date(y, m, dd, 0, 0, 0, 0);
  };

  const anchoredWindowForSelection = (yearNum, monthIdx, reportDay) => {
    const start = clampStartOfDay(yearNum, monthIdx - 1, reportDay);
    const end   = clampStartOfDay(yearNum, monthIdx,     reportDay);
    return { start, end };
  };

  // -----------------------------------------
  // Filter for pairing (buffered)
  // -----------------------------------------
  const BUFFER_MS = 2 * 24 * 3600 * 1000; // plus-minus 2 days

  const windows = useMemo(() => {
    if (selectedYear === "All" || selectedMonth === "All") return null;
    const yearNum = parseInt(selectedYear, 10);
    const monthIdx = parseInt(selectedMonth, 10);
    const reportDay = getTimesheetDay();
    const { start, end } = anchoredWindowForSelection(yearNum, monthIdx, reportDay);
    const startBuffered = new Date(start.getTime() - BUFFER_MS);
    const endBuffered   = new Date(end.getTime() + BUFFER_MS);
    return { start, end, startBuffered, endBuffered };
  }, [selectedYear, selectedMonth]);

  const allowTotals = selectedStaff !== "All" && selectedMonth !== "All" && selectedYear !== "All";

  const baseFilteredForPairing = useMemo(() => {
    return data.filter((item) => {
      if (selectedPharmacy !== "All" && item.pharmacyName !== selectedPharmacy) return false;
      if (windows) {
        const created = item.createdAt ? new Date(item.createdAt) : null;
        if (!created || isNaN(created)) return false;
        if (!(created >= windows.startBuffered && created < windows.endBuffered)) return false;
      }
      if (searchText) {
        const st = searchText.toLowerCase();
        const ok = Object.values(item).some((val) => val && val.toString().toLowerCase().includes(st));
        if (!ok) return false;
      }
      return true;
    });
  }, [data, selectedPharmacy, windows, searchText]);

  const staffNameOptions = useMemo(() => {
    return Array.from(new Set(baseFilteredForPairing.map((x) => x.staffName || "Unknown")))
      .sort((a, b) => a.localeCompare(b));
  }, [baseFilteredForPairing]);

  const filteredData = useMemo(
    () => baseFilteredForPairing.filter((item) => (selectedStaff === "All" ? true : (item.staffName || "Unknown") === selectedStaff)),
    [baseFilteredForPairing, selectedStaff]
  );

  // -----------------------------------------
  // Build rows (pair IN->OUT) and compute break-aware durations
  // -----------------------------------------
  const rows = useMemo(() => {
    const logsByStaff = {};
    filteredData
      .slice()
      .sort((a, b) => {
        const sa = (a.staffName || "");
        const sb = (b.staffName || "");
        if (sa !== sb) return sa.localeCompare(sb);
        return new Date(a.createdAt) - new Date(b.createdAt);
      })
      .forEach((it) => {
        const staff = it.staffName || "Unknown";
        (logsByStaff[staff] ||= []).push(it);
      });

    const outRows = [];

    Object.entries(logsByStaff).forEach(([staffName, items]) => {
      const sessions = [];
      let pendingIn = null;

      for (const e of items) {
        const hasIn = !!e.clockIn;
        const hasOut = !!e.clockOut;
        if (hasIn && hasOut) {
          sessions.push({ in: e, out: e });
          pendingIn = null;
        } else if (hasIn && !hasOut) {
          pendingIn = e;
        } else if (!hasIn && hasOut) {
          if (pendingIn) { sessions.push({ in: pendingIn, out: e }); pendingIn = null; }
          else { sessions.push({ in: null, out: e }); }
        }
      }
      if (pendingIn) sessions.push({ in: pendingIn, out: null });

      const byDate = new Map();
      for (const s of sessions) {
        const inDT = s.in?.clockIn ? new Date(s.in.clockIn) : null;
        const outDT = s.out?.clockOut ? new Date(s.out.clockOut) : null;
        const anchor = inDT || outDT || new Date(s.in?.createdAt || s.out?.createdAt || Date.now());
        if (windows) { if (!(anchor >= windows.start && anchor < windows.end)) continue; }
        const dateStr = new Date(anchor).toISOString().slice(0, 10);
        if (!byDate.has(dateStr)) byDate.set(dateStr, []);
        byDate.get(dateStr).push(s);
      }

      const dateKeys = Array.from(byDate.keys()).sort((a, b) => new Date(a) - new Date(b));

      let runningTotal = 0;
      let anyNKOverall = false;

      dateKeys.forEach((dateStr) => {
        const daySessions = byDate.get(dateStr) || [];
        const dayOfWeek = new Date(dateStr).toLocaleString("en-GB", { weekday: "short" });

        const sessionsText = [];
        const hrsPerSession = [];
        const notesBlocks = [];
        const notesEditedFlags = [];
        const distPairs = [];
        const discrepancyFlags = [];

        let dayTotal = 0;
        let dayHasNK = false;

        daySessions.forEach(({ in: entryIn, out: entryOut }) => {
          const inDT = entryIn?.clockIn ? new Date(entryIn.clockIn) : null;
          const outDT = entryOut?.clockOut ? new Date(entryOut.clockOut) : null;

          const inStr = inDT ? formatHHMMNoTZ(inDT) : "00:00";
          const baseLabel = outDT ? `${inStr}-${formatHHMMNoTZ(outDT)}` : `${inStr} - Clock out awaited`;

          // Breaks for this session
          const attId = String(entryIn?._attendanceLogId || entryOut?._attendanceLogId || "");
          const breaks = attId && breaksByAttId[attId] ? breaksByAttId[attId] : [];
          let breaksHours = 0;
          const breakParts = [];
          for (const b of breaks) {
            const bs = b.breakStart ? new Date(b.breakStart) : null;
            const be = b.breakEnd ? new Date(b.breakEnd) : null;
            const dur = diffHours(bs, be);
            breaksHours += dur;
            breakParts.push(`${bs ? formatHHMMNoTZ(bs) : "?"}-${be ? formatHHMMNoTZ(be) : "?"}${dur ? " (" + dur.toFixed(2) + "h)" : ""}`);
          }
          const sessionLabel = breakParts.length ? `${baseLabel} (breaks: ${breakParts.join(", ")})` : baseLabel;
          sessionsText.push(sessionLabel);

          let durationStr = "n/k";
          let discrepancy = false;
          if (inDT && outDT) {
            const inDay = inDT.toISOString().slice(0, 10);
            const outDay = outDT.toISOString().slice(0, 10);
            if (inDay !== outDay) {
              durationStr = "Clock out day discrepancy";
              discrepancy = true;
              anyNKOverall = true;
            } else {
              let duration = diffHours(inDT, outDT);
              if (breaksHours > 0) duration = Math.max(0, duration - breaksHours);
              durationStr = duration.toFixed(2);
              dayTotal += duration;
              runningTotal += duration;
            }
          } else {
            dayHasNK = true;
            anyNKOverall = true;
          }
          hrsPerSession.push(durationStr);
          discrepancyFlags.push(discrepancy);

          const lines = [
            ...(entryIn?.notes ? String(entryIn.notes).split("\n") : []),
            ...(entryOut?.notes ? String(entryOut.notes).split("\n") : []),
          ].map((s) => s.trim()).filter(Boolean);
          const deduped = Array.from(new Set(lines));
          const combinedBlock = deduped.join("\n");
          notesBlocks.push(combinedBlock);
          notesEditedFlags.push(/\b\d{2}\/\d{2}\/\d{4},\s*\d{2}:\d{2}:\d{2}\b/.test(combinedBlock));

          const phLat = toNumOrNull(entryIn?.pharmacyLat ?? entryOut?.pharmacyLat);
          const phLong = toNumOrNull(entryIn?.pharmacyLong ?? entryOut?.pharmacyLong);
          const inDist  = (entryIn && phLat != null && phLong != null && entryIn.lat != null && entryIn.long != null)
            ? haversineMeters(entryIn.lat, entryIn.long, phLat, phLong) : null;
          const outDist = (entryOut && phLat != null && phLong != null && entryOut.lat != null && entryOut.long != null)
            ? haversineMeters(entryOut.lat, entryOut.long, phLat, phLong) : null;
          distPairs.push({ in: inDist, out: outDist });
        });

        const pharmacyName = (daySessions[0]?.in?.pharmacyName || daySessions[0]?.out?.pharmacyName || "");

        outRows.push({
          staffName,
          pharmacyName,
          date: dateStr,
          dayOfWeek,
          sessions: sessionsText,
          hoursWorkedPerSession: hrsPerSession,
          notesBlocks,
          notesEditedFlags,
          distancePairs: distPairs,
          discrepancyFlags,
          incomplete: dayHasNK,
          totalHours: dayHasNK ? "n/k" : dayTotal.toFixed(2),
          rawEntries: daySessions.flatMap(({ in: ein, out: eout }) => [ein || null, eout || null]),
        });
      });

      if (allowTotals) {
        outRows.push({
          staffName,
          pharmacyName: "",
          date: "Totals",
          dayOfWeek: "",
          sessions: [],
          hoursWorkedPerSession: [],
          notesBlocks: [],
          notesEditedFlags: [],
          distancePairs: [],
          discrepancyFlags: [],
          incomplete: anyNKOverall,
          totalHours: anyNKOverall ? "n/k" : runningTotal.toFixed(2),
          rawEntries: [],
        });
      }
    });

    return outRows;
  }, [filteredData, windows, allowTotals, breaksByAttId]);

  // -----------------------------------------
  // Sort / Visible
  // -----------------------------------------
  const detailRows = rows.filter((r) => r.date !== "Totals");
  const totalRows = rows.filter((r) => r.date === "Totals");

  const cmp = (a, b) => {
    let av, bv;
    if (sortBy === "date")       { av = new Date(a.date).getTime();       bv = new Date(b.date).getTime(); }
    else if (sortBy === "pharmacyName") { av = (a.pharmacyName || "").toLowerCase(); bv = (b.pharmacyName || "").toLowerCase(); }
    else                         { av = (a.staffName || "").toLowerCase(); bv = (b.staffName || "").toLowerCase(); }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ?  1 : -1;
    if (a.staffName !== b.staffName) return a.staffName < b.staffName ? -1 : 1;
    if (a.date !== b.date) return new Date(a.date) - new Date(b.date);
    return 0;
  };

  if (selectedStaff !== "All") { detailRows.sort(cmp); }

  const isNumericString = (s) => { if (s === null || s === undefined) return false; const t = String(s).trim(); return /^-?\d+(\.\d+)?$/.test(t); };
  const hoursIsValid = (h) => isNumericString(h) && parseFloat(h) > 0;
  const isInvalidHoursVal = (h) => !hoursIsValid(h);
  const rowHasInvalidHours = (r) => { if (r.date === "Totals") return isInvalidHoursVal(r.totalHours); return (r.hoursWorkedPerSession || []).some(isInvalidHoursVal); };
  const rowHasNotes = (r) => { const arr = r.notesBlocks || []; for (const s of arr) { if (String(s || "").trim() !== "") return true; } return false; };

  let visibleRows = (showDetails ? rows.slice() : totalRows.slice());
  if (onlyShowIssues) visibleRows = visibleRows.filter((r) => rowHasInvalidHours(r) || rowHasNotes(r));

  // -----------------------------------------
  // Hover details content builder
  // -----------------------------------------
  const buildHoverCard = (row, sessionIdx) => {
    const iIn = sessionIdx * 2, iOut = iIn + 1;
    const entryIn = row.rawEntries[iIn];
    const entryOut = row.rawEntries[iOut];
    const inDt = entryIn?.clockIn ? new Date(entryIn.clockIn) : null;
    const outDt = entryOut?.clockOut ? new Date(entryOut.clockOut) : null;

    // Calculate totals
    const attId = String(entryIn?._attendanceLogId || entryOut?._attendanceLogId || "");
    const breaks = attId && breaksByAttId[attId] ? breaksByAttId[attId] : [];
    let onSite = diffHours(inDt, outDt);
    let breaksTot = 0;
    const breakLines = [];
    for (let k = 0; k < breaks.length; k++) {
      const b = breaks[k];
      const bs = b.breakStart ? new Date(b.breakStart) : null;
      const be = b.breakEnd ? new Date(b.breakEnd) : null;
      const dur = diffHours(bs, be);
      breaksTot += dur;
      const notePart = (b.notes && String(b.notes).trim()) ? `  Notes: ${String(b.notes).trim()}` : "";
      breakLines.push(`Break ${k + 1}: ${bs ? formatHHMMNoTZ(bs) : "?"}-${be ? formatHHMMNoTZ(be) : "?"}${dur ? " (" + dur.toFixed(2) + "h)" : ""}${notePart}`);
    }
    const worked = Math.max(0, onSite - breaksTot);

    return (
      <div className="hover-card">
        <div className="hover-title">Session details</div>
        <div className="hover-line">Clock in: {inDt ? formatHHMMNoTZ(inDt) : "-"}</div>
        <div className="hover-line">Clock out: {outDt ? formatHHMMNoTZ(outDt) : "-"}</div>
        {breakLines.map((ln, i) => (<div key={i} className="hover-line">{ln}</div>))}
        <div className="hover-total">Total on site: {onSite ? onSite.toFixed(2) : "0.00"} h</div>
        <div className="hover-total">Total breaks taken: {breaksTot ? breaksTot.toFixed(2) : "0.00"} h</div>
        <div className="hover-total">Total hours worked: {worked ? worked.toFixed(2) : "0.00"} h</div>
      </div>
    );
  };

  // -----------------------------------------
  // Helpers
  // -----------------------------------------
  const monthName = selectedMonth === "All" ? "All months" : new Date(0, parseInt(selectedMonth || "0", 10)).toLocaleString("default", { month: "long" });
  const yearLabel = selectedYear === "All" ? "All years" : selectedYear;

  const now = new Date();
  const generatedAt = now.toLocaleString("en-GB", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const matchesOriginal = (x, orig) => {
    const idMatch = Boolean(orig?._id && x?._id && String(x._id) === String(orig._id));
    const keyMatch = Boolean(!!orig?.createdAt && x.createdAt === orig.createdAt && x.staffName === orig.staffName && x.pharmacyName === orig.pharmacyName);
    return Boolean(idMatch || keyMatch);
  };

  const handleSortClick = (key) => { if (selectedStaff === "All") return; if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy(key); setSortDir("asc"); } };
  const sortArrow = (key) => (sortBy === key ? <span className="sort-ind">{sortDir === "asc" ? "▲" : "▼"}</span> : null);

  // -----------------------------------------
  // Map helpers (unchanged behavior)
  // -----------------------------------------
  const loadLeafletOnce = () => new Promise((resolve, reject) => {
    if (window.L) return resolve(window.L);
    const link = document.createElement("link"); link.rel = "stylesheet"; link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    const script = document.createElement("script"); script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"; script.onload = () => resolve(window.L); script.onerror = reject; document.head.appendChild(link); document.body.appendChild(script);
  });

  const renderMapMarkers = () => {
    if (!mapInstanceRef.current || !mapInfo || !window.L) return;
    const L = window.L;

    if (mapMarkersRef.current) {
      mapMarkersRef.current.remove();
      mapMarkersRef.current = null;
    }
    const group = L.layerGroup().addTo(mapInstanceRef.current);
    mapMarkersRef.current = group;

    const ph = L.marker([mapInfo.pharmacyLat, mapInfo.pharmacyLong], { title: "Pharmacy" }).addTo(group);
    ph.bindTooltip("Pharmacy", { permanent: true, direction: "top", offset: [0, -6] });

    const bounds = L.latLngBounds([[mapInfo.pharmacyLat, mapInfo.pharmacyLong]]);

    if (mapMode === "in" && mapInfo.inLat != null && mapInfo.inLong != null) {
      const mIn = L.marker([mapInfo.inLat, mapInfo.inLong]).addTo(group);
      mIn.bindTooltip(`IN ${mapInfo.inWhen || "-"}`, { permanent: true, direction: "top", offset: [0, -6] });
      bounds.extend([mapInfo.inLat, mapInfo.inLong]);
    }
    if (mapMode === "out" && mapInfo.outLat != null && mapInfo.outLong != null) {
      const mOut = L.marker([mapInfo.outLat, mapInfo.outLong]).addTo(group);
      mOut.bindTooltip(`OUT ${mapInfo.outWhen || "-"}`, { permanent: true, direction: "top", offset: [0, -6] });
      bounds.extend([mapInfo.outLat, mapInfo.outLong]);
    }

    mapInstanceRef.current.fitBounds(bounds.pad(0.25));
  };

  const openMapForSession = async (row, sessionIdx) => {
    if (!row || row.date === "Totals") return;
    const iIn = sessionIdx * 2, iOut = iIn + 1;
    const eIn = row.rawEntries[iIn];
    const eOut = row.rawEntries[iOut];

    const phLat = toNumOrNull(eIn?.pharmacyLat ?? eOut?.pharmacyLat);
    const phLong = toNumOrNull(eIn?.pharmacyLong ?? eOut?.pharmacyLong);

    const inLat = toNumOrNull(eIn?.lat);
    const inLong = toNumOrNull(eIn?.long);
    const outLat = toNumOrNull(eOut?.lat);
    const outLong = toNumOrNull(eOut?.long);

    if (phLat == null || phLong == null) { alert("Missing pharmacy coordinates."); return; }
    if (inLat == null && outLat == null) { alert("No staff coordinates for this session."); return; }

    const inWhen = eIn?.clockIn ? formatHHMMNoTZ(eIn.clockIn) : "-";
    const outWhen = eOut?.clockOut ? formatHHMMNoTZ(eOut.clockOut) : "-";

    setMapInfo({
      pharmacyLat: phLat, pharmacyLong: phLong,
      inLat, inLong, outLat, outLong,
      inWhen, outWhen,
      label: `${displayName(row.staffName, masked)} — ${new Date(row.date).toLocaleDateString("en-GB")} (${row.dayOfWeek})`
    });
    setMapMode(inLat != null ? "in" : "out");
    setMapOpen(true);
  };

  useEffect(() => {
    if (!mapOpen) return;
    (async () => {
      try {
        const L = await loadLeafletOnce();
        if (!mapDivRef.current) return;
        if (!mapInstanceRef.current) {
          const map = L.map(mapDivRef.current);
          mapInstanceRef.current = map;
          L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            maxZoom: 19,
            attribution: "OpenStreetMap contributors",
          }).addTo(map);
          requestAnimationFrame(() => {
            if (!mapInstanceRef.current) return;
            mapInstanceRef.current.invalidateSize();
            renderMapMarkers();
          });
        } else {
          renderMapMarkers();
        }
      } catch (e) {
        console.error("Failed to init map:", e);
      }
    })();
  }, [mapOpen, mapMode, mapInfo]);

  useEffect(() => {
    if (!mapOpen && mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
      mapMarkersRef.current = null;
    }
  }, [mapOpen]);

  // -----------------------------------------
  // Export helpers
  // -----------------------------------------
  const computeStaffTotals = (staffRows) => {
    let hasNK = false;
    let sum = 0;
    for (const row of staffRows) {
      const arr = row.hoursWorkedPerSession || [];
      for (const h of arr) {
        if (!hoursIsValid(h)) { hasNK = true; continue; }
        const n = Number(h);
        if (Number.isFinite(n)) sum += n;
      }
    }
    return { hasNK, totalStr: hasNK ? "n/k" : sum.toFixed(2) };
  };

  // -----------------------------------------
  // Export: Excel (enhanced with breaks, subtotals)
  // -----------------------------------------
  const sanitizeSheetName = (name) => {
    const bad = /[\\\/?*\[\]:]/g;
    const base = String(name || "Sheet").replace(bad, "-").trim() || "Sheet";
    return base.slice(0, 31);
  };
  const uniqueSheetName = (base, used) => {
    let name = sanitizeSheetName(base);
    if (!used.has(name)) { used.add(name); return name; }
    for (let i = 2; i < 1000; i++) {
      const tryName = sanitizeSheetName(`${base} ${i}`);
      if (!used.has(tryName)) { used.add(tryName); return tryName; }
    }
    const fallback = sanitizeSheetName(`${base} ${Date.now().toString().slice(-5)}`);
    used.add(fallback);
    return fallback;
  };

  const exportToExcel = () => {
    if (selectedMonth === "All" || selectedYear === "All") {
      alert("Please select a specific Month and Year before exporting to Excel.");
      return;
    }

    const reportDay = getTimesheetDay();
    const yearNum = parseInt(selectedYear, 10);
    const monthIdx = parseInt(selectedMonth, 10);
    const { start, end } = anchoredWindowForSelection(yearNum, monthIdx, reportDay);
    const startBuffered = new Date(start.getTime() - BUFFER_MS);
    const endBuffered = new Date(end.getTime() + BUFFER_MS);

    const logsInWindow = data.filter((item) => {
      const created = item.createdAt ? new Date(item.createdAt) : null;
      if (!created || isNaN(created.getTime())) return false;
      if (!(created >= startBuffered && created < endBuffered)) return false;
      if (selectedPharmacy !== "All" && item.pharmacyName !== selectedPharmacy) return false;
      return true;
    });

    if (logsInWindow.length === 0) {
      alert("No data for the selected month/year and filters.");
      return;
    }

    const byStaff = new Map();
    for (const it of logsInWindow) {
      const staff = it.staffName || "Unknown";
      if (!byStaff.has(staff)) byStaff.set(staff, []);
      byStaff.get(staff).push(it);
    }

    const staffList = Array.from(byStaff.entries()).map(([staff, items]) => {
      const counts = {};
      for (const it of items) counts[it.pharmacyName || "Unknown"] = (counts[it.pharmacyName || "Unknown"] || 0) + 1;
      const primaryPharmacy = Object.keys(counts)
        .sort((a, b) => counts[b] - counts[a] || a.localeCompare(b))[0] || "";
      items.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      return { staff, items, primaryPharmacy };
    });

    staffList.sort((A, B) => {
      const pa = (A.primaryPharmacy || "").toLowerCase();
      const pb = (B.primaryPharmacy || "").toLowerCase();
      if (pa < pb) return -1;
      if (pa > pb) return 1;
      return (A.staff || "").toLowerCase().localeCompare((B.staff || "").toLowerCase());
    });

    const wb = XLSX.utils.book_new();
    const usedSheetNames = new Set();

    const headerRow = [
      "Date","Pharmacy","Name","Clock in","Clock out",
      "On site (h)","Breaks (h)","Worked (h)",
      "Notes","Break details","Distance (m)"
    ];

    const fileMonth = new Date(0, monthIdx).toLocaleString("default", { month: "long" });
    const fileYear = selectedYear;
    const startMonthName = start.toLocaleString("default", { month: "long" });
    const startYear = start.getFullYear();

    staffList.forEach(({ staff, items, primaryPharmacy }) => {
      const sessions = [];
      let pendingIn = null;
      for (const e of items) {
        const hasIn = !!e.clockIn, hasOut = !!e.clockOut;
        if (hasIn && hasOut) { sessions.push({ in: e, out: e }); pendingIn = null; }
        else if (hasIn) pendingIn = e;
        else if (hasOut) { sessions.push({ in: pendingIn, out: e }); pendingIn = null; }
      }
      if (pendingIn) sessions.push({ in: pendingIn, out: null });

      const kept = sessions.filter((s) => {
        const anchor = s.in?.clockIn ? new Date(s.in.clockIn) : s.out?.clockOut ? new Date(s.out.clockOut) : new Date(s.in?.createdAt || s.out?.createdAt || Date.now());
        return anchor >= start && anchor < end;
      });

      const bucket = new Map();
      for (const s of kept) {
        const anchor = s.in?.clockIn ? new Date(s.in.clockIn) : s.out?.clockOut ? new Date(s.out.clockOut) : new Date(s.in?.createdAt || s.out?.createdAt || Date.now());
        const key = anchor.toISOString().slice(0, 10);
        (bucket.get(key) || bucket.set(key, []).get(key)).push(s);
      }

      const aoa = [];
      aoa.push([`Group: ${organisationName}`]);
      aoa.push([`Generated: ${generatedAt}`]);
      aoa.push([`Staff: ${staff} (${primaryPharmacy || ""})`]);
      aoa.push([`Period: ${getTimesheetDay()} ${startMonthName} ${startYear} -> ${getTimesheetDay()} ${fileMonth} ${fileYear}`]);
      aoa.push([]);
      aoa.push(headerRow);

      let sumOnSite = 0;
      let sumBreaks = 0;
      let sumWorked = 0;
      let anyNK = false;

      const dateKeys = Array.from(bucket.keys()).sort((a, b) => new Date(a) - new Date(b));
      dateKeys.forEach((dateStr) => {
        const dayOfWeek = new Date(dateStr).toLocaleString("en-GB", { weekday: "short" });
        const list = bucket.get(dateStr) || [];

        let dayOnSite = 0;
        let dayBreaks = 0;
        let dayWorked = 0;

        list.forEach(({ in: entryIn, out: entryOut }) => {
          const cin = entryIn?.clockIn ? formatHHMMNoTZ(entryIn.clockIn) : "";
          const cout = entryOut?.clockOut ? formatHHMMNoTZ(entryOut.clockOut) : "";

          const inDt = entryIn?.clockIn ? new Date(entryIn.clockIn) : null;
          const outDt = entryOut?.clockOut ? new Date(entryOut.clockOut) : null;

          const attId = String(entryIn?._attendanceLogId || entryOut?._attendanceLogId || "");
          const breaks = attId && breaksByAttId[attId] ? breaksByAttId[attId] : [];
          let breaksHours = 0; let breaksList = [];
          for (const b of breaks) {
            const bs = b.breakStart ? new Date(b.breakStart) : null;
            const be = b.breakEnd ? new Date(b.breakEnd) : null;
            const dur = diffHours(bs, be);
            breaksHours += dur;
            const notePart = (b.notes && String(b.notes).trim()) ? ` (Notes: ${String(b.notes).trim()})` : "";
            breaksList.push(`${bs ? formatHHMMNoTZ(bs) : "?"}-${be ? formatHHMMNoTZ(be) : "?"}${dur ? " (" + dur.toFixed(2) + "h)" : ""}${notePart}`);
          }

          const lines = [
            ...(entryIn?.notes ? String(entryIn.notes).split("\n") : []),
            ...(entryOut?.notes ? String(entryOut.notes).split("\n") : []),
          ].map(s => s.trim()).filter(Boolean);
          const deduped = Array.from(new Set(lines)).join("\n");

          const phLat = toNumOrNull(entryIn?.pharmacyLat ?? entryOut?.pharmacyLat);
          const phLong = toNumOrNull(entryIn?.pharmacyLong ?? entryOut?.pharmacyLong);
          const inDist  = (entryIn && phLat != null && phLong != null && entryIn.lat != null && entryIn.long != null) ? haversineMeters(entryIn.lat, entryIn.long, phLat, phLong) : null;
          const outDist = (entryOut && phLat != null && phLong != null && entryOut.lat != null && entryOut.long != null) ? haversineMeters(entryOut.lat, entryOut.long, phLat, phLong) : null;
          const distText = `In: ${inDist == null ? "" : `${inDist} m`}  Out: ${outDist == null ? "" : `${outDist} m`}`.trim();

          let onSite = diffHours(inDt, outDt);
          let worked = 0;
          if (entryIn?.clockIn && entryOut?.clockOut) {
            const inDay = new Date(entryIn.clockIn).toISOString().slice(0,10);
            const outDay = new Date(entryOut.clockOut).toISOString().slice(0,10);
            if (inDay !== outDay) { anyNK = true; worked = 0; }
            else { worked = Math.max(0, onSite - breaksHours); }
          } else {
            anyNK = true;
          }

          dayOnSite += onSite;
          dayBreaks += breaksHours;
          dayWorked += worked;

          aoa.push([
            `${new Date(dateStr).toLocaleDateString("en-GB")} (${dayOfWeek})`,
            entryIn?.pharmacyName || entryOut?.pharmacyName || primaryPharmacy || "",
            staff,
            cin,
            cout,
            onSite ? onSite.toFixed(2) : "",
            breaksHours ? breaksHours.toFixed(2) : "",
            worked ? worked.toFixed(2) : (entryIn?.clockIn && entryOut?.clockOut ? "0.00" : "n/k"),
            deduped,
            breaksList.join(" | "),
            distText
          ]);
        });

        // Daily subtotal row
        aoa.push([
          `Daily totals for ${new Date(dateStr).toLocaleDateString("en-GB")} (${dayOfWeek})`,
          "", "", "", "",
          dayOnSite.toFixed(2),
          dayBreaks.toFixed(2),
          dayWorked.toFixed(2),
          "", "", ""
        ]);

        sumOnSite += dayOnSite;
        sumBreaks += dayBreaks;
        sumWorked += dayWorked;

        // Spacer
        aoa.push([]);
      });

      // Period total row
      aoa.push([
        `Period totals for ${staff}`,
        "", "", "", "",
        sumOnSite.toFixed(2),
        sumBreaks.toFixed(2),
        anyNK ? "n/k" : sumWorked.toFixed(2),
        "", "", ""
      ]);

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = [
        { wch: 28 }, { wch: 24 }, { wch: 26 }, { wch: 10 }, { wch: 10 },
        { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 50 }, { wch: 40 }, { wch: 22 },
      ];

      const sheetName = uniqueSheetName(staff || "Unknown", usedSheetNames);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    const filename = `Staff_Attendance_${organisationName}_${fileMonth}_${fileYear}.xlsx`.replace(/\s+/g, "_");
    const wbout = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, filename);
  };

  // -----------------------------------------
  // UI
  // -----------------------------------------
  const yearsList = Array.from({ length: 10 }, (_, i) => String(new Date().getFullYear() - i));

  return (
    <div className="stafflogin-container">
      <style>{staffLogsStyles}</style>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2>Staff Attendance Logs (DEV)</h2>
        <MaskToggleButton />
      </div>

      <div className="filters-row" style={{ textAlign: "left", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end" }}>
        <label>
          Pharmacy
          <select value={selectedPharmacy} onChange={(e) => setSelectedPharmacy(e.target.value)}>
            <option value="All">All Pharmacies</option>
            {members.map((m, i) => (
              <option key={i} value={m.pharmacyName}>{displayName(m.pharmacyName, masked)}</option>
            ))}
          </select>
        </label>

        <label>
          Month
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            <option value="All">All months</option>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i} value={String(i)}>
                {new Date(0, i).toLocaleString("default", { month: "long" })}
              </option>
            ))}
          </select>
        </label>

        <label>
          Year
          <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
            <option value="All">All years</option>
            {yearsList.map((y) => (<option key={y} value={y}>{y}</option>))}
          </select>
        </label>

        <label className="checkbox-label" style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="checkbox" checked={showDetails} onChange={(e) => setShowDetails(e.target.checked)} />
          Show details
        </label>

        <label className="checkbox-label" style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="checkbox"
            checked={onlyShowIssues}
            onChange={(e) => setOnlyShowIssues(e.target.checked)}
          />
          Only show issues (invalid hours or notes)
        </label>

        <label>
          Staff name
          <select value={selectedStaff} onChange={(e) => setSelectedStaff(e.target.value)}>
            <option value="All">All</option>
            {staffNameOptions.map((n) => (<option key={n} value={n}>{displayName(n, masked)}</option>))}
          </select>
        </label>

        <label style={{ marginLeft: "auto" }}>
          Search
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search..."
          />
        </label>

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={exportToExcel}>Export to Excel</button>
        </div>
      </div>

      {lastChangeSummary && (
        <div style={{ marginTop: 8, padding: "8px 10px", background: "#f0f9ff", border: "1px solid #bee3f8", borderRadius: 6 }}>
          <strong>Last change:</strong> {lastChangeSummary}
        </div>
      )}

      <div className="table-wrapper" style={{ marginTop: 8 }}>
        <table className="data-table staff-logs">
          <colgroup>
            <col style={{ width: "150px" }} />
            <col style={{ width: "180px" }} />
            <col style={{ width: "220px" }} />
            <col style={{ width: "260px" }} />
            <col style={{ width: "140px" }} />
            <col style={{ width: "300px" }} />
            <col style={{ width: "220px" }} />
          </colgroup>
          <thead>
            <tr>
              <th onClick={() => handleSortClick("date")}><span className="th-wrap"><span className="label">Date</span>{sortArrow("date")}</span></th>
              <th onClick={() => handleSortClick("pharmacyName")}><span className="th-wrap"><span className="label">Pharmacy</span>{sortArrow("pharmacyName")}</span></th>
              <th onClick={() => handleSortClick("staffName")}><span className="th-wrap"><span className="label">Name</span>{sortArrow("staffName")}</span></th>
              <th className="nosort">Sessions</th>
              <th className="nosort">Hrs worked</th>
              <th className="nosort">Notes</th>
              <th className="nosort">Distance</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const out = [];
              for (let idx = 0; idx < visibleRows.length; idx++) {
                const row = visibleRows[idx];

                if (row.date === "Totals") {
                  const highlight = row.incomplete;
                  out.push(
                    <tr key={`totals-${idx}`} data-highlight={highlight ? "true" : "false"}>
                      <td>Totals</td><td></td><td>{displayName(row.staffName, masked)}</td><td></td><td>{row.totalHours}</td><td></td><td></td>
                    </tr>
                  );
                  out.push(
                    <tr key={`divider-${idx}`} className="divider-row">
                      <td colSpan={7}>-- End of {displayName(row.staffName, masked)} entry --</td>
                    </tr>
                  );
                  continue;
                }

                const n = Math.max(1, row.sessions.length);
                for (let i = 0; i < n; i++) {
                  const segClass = i > 0 ? "seg" : "";
                  const hoursVal = row.hoursWorkedPerSession[i] ?? "";
                  const invalid = isInvalidHoursVal(hoursVal);
                  const edited = row.notesEditedFlags[i];

                  const pair = row.distancePairs?.[i] || { in: null, out: null };
                  const over50 = (Number(pair.in) >= 50) || (Number(pair.out) >= 50);
                  const distClass = over50 ? "dist-coral" : "";

                  out.push(
                    <tr key={`${idx}-seg-${i}`} className={invalid ? "row-invalid" : ""}>
                      {i === 0 && (<><td rowSpan={n}>{`${new Date(row.date).toLocaleDateString("en-GB")} (${row.dayOfWeek})`}</td>
                                     <td rowSpan={n}>{displayName(row.pharmacyName, masked)}</td>
                                     <td rowSpan={n}>{displayName(row.staffName, masked)}</td></>)}
                      <td className={segClass}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span className="hover-wrap">
                            <span>{row.sessions[i] || ""}</span>
                            {buildHoverCard(row, i)}
                          </span>
                          {ALLOW_EDIT && (
                            <button
                              className="edit-icon-btn"
                              title="Edit session"
                              onClick={() => {}}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </td>
                      <td className={`${segClass} ${hoursVal === "n/k" ? "nk" : ""}`}>{hoursVal}</td>
                      <td className={`${segClass} ${edited ? "amber" : ""}`}>
                        {(row.notesBlocks[i] || "").split("\n").map((ln, k) => <div key={k}>{ln}</div>)}
                      </td>
                      <td
                        className={`${segClass} ${distClass} dist-cell`}
                        title="Click to view map"
                        onClick={() => openMapForSession(row, i)}
                      >
                        {`In: ${pair.in == null ? "-" : `${pair.in} m`}  Out: ${pair.out == null ? "-" : `${pair.out} m`}`}
                      </td>
                    </tr>
                  );
                }
              }
              return out;
            })()}
            {visibleRows.length === 0 && (
              <tr><td colSpan={7}>No data.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* MAP MODAL */}
      {mapOpen && mapInfo && (
        <div className="map-backdrop" onClick={(e) => { if (e.target.classList.contains("map-backdrop")) setMapOpen(false); }}>
          <div className="map-card">
            <div className="map-header">
              <h3 className="map-title">Location - {mapInfo.label}</h3>
              <button className="btn" onClick={() => setMapOpen(false)}>Close</button>
            </div>
            <div className="map-body">
              <div style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 16 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="radio"
                    name="mapMode"
                    value="in"
                    checked={mapMode === "in"}
                    onChange={() => setMapMode("in")}
                    disabled={mapInfo.inLat == null || mapInfo.inLong == null}
                  />
                  Clock in
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="radio"
                    name="mapMode"
                    value="out"
                    checked={mapMode === "out"}
                    onChange={() => setMapMode("out")}
                    disabled={mapInfo.outLat == null || mapInfo.outLong == null}
                  />
                  Clock out
                </label>
              </div>

              <div id="attendance-map" ref={mapDivRef} />
              <div style={{ marginTop: 8, fontSize: 12, color: "#374151" }}>
                <strong>Legend:</strong> Pharmacy marker and In/Out markers
              </div>
            </div>
            <div className="map-footer">
              <button className="btn" onClick={() => setMapOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffDev;
